// components/common/skeletons/VideoCardSkeleton/VideoCardSkeleton.tsx

"use client";

import React from "react";
import "./VideoCardSkeleton.css";

export interface VideoCardSkeletonProps {
  variant?: "portrait" | "horizontal" | "square" | "banner" | "vertical" | "fullVertical" | "ultrawide";
  showTitle?: boolean;
  className?: string;
  /** Keep legacy overlay behavior (absolute/inset:0). Set true to use aspect-ratio sizing. */
  standalone?: boolean;
  /** Force overlay meta; defaults to true for ultrawide, else false. */
  forceOverlayMeta?: boolean;
}

const aspectRatios: Record<string, string> = {
  portrait: "aspect-9-16",
  horizontal: "aspect-16-9",
  square: "aspect-1-1",
  banner: "aspect-4-1",
  vertical: "aspect-2-3",
  fullVertical: "aspect-full-vertical",
  ultrawide: "aspect-21-9",
};

const VideoCardSkeleton: React.FC<VideoCardSkeletonProps> = ({
  variant = "ultrawide",
  showTitle = true,
  className = "",
  standalone = false,
  forceOverlayMeta,
}) => {
  const ratioClass = aspectRatios[variant] || aspectRatios.ultrawide;
  const useOverlayMeta = typeof forceOverlayMeta === "boolean"
    ? forceOverlayMeta
    : variant === "ultrawide" || variant === "banner";

  return (
    <div
      className={[
        "video-card-skeleton",
        ratioClass,
        standalone ? "video-card-skeleton--standalone" : "",
        useOverlayMeta ? "video-card-skeleton--overlay-meta" : "",
        className || "",
      ].join(" ").trim()}
    >
      <div className="video-card-skeleton-thumbnail" />

      <div className={`video-card-skeleton-meta ${useOverlayMeta ? "overlay-meta" : ""}`}>
        {showTitle && <div className="video-card-skeleton-title" />}

        {(variant === "horizontal" || variant === "ultrawide") && (
          <>
            <div className="video-card-skeleton-subtitle" />
            <div className="video-card-skeleton-action" />
          </>
        )}

        {variant === "portrait" && (
          <>
            <div className="video-card-skeleton-avatar" />
            <div className="video-card-skeleton-subtitle" />
          </>
        )}

        {variant === "square" && <div className="video-card-skeleton-subtitle" />}

        {variant === "banner" && (
          <>
            <div className="video-card-skeleton-title" />
            <div className="video-card-skeleton-action" />
          </>
        )}

        {variant === "vertical" && (
          <>
            <div className="video-card-skeleton-avatar" />
            <div className="video-card-skeleton-subtitle" />
          </>
        )}

        {variant === "fullVertical" && (
          <>
            <div className="video-card-skeleton-avatar" />
            <div className="video-card-skeleton-subtitle" />
            <div className="video-card-skeleton-action" />
          </>
        )}
      </div>
    </div>
  );
};

export default VideoCardSkeleton;
